import * as React from 'react'
import { useMemo, useState } from 'react'
import clsx from 'clsx'
import Link from 'next/link'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import { ArrowRight } from 'svgIcons'
import { getIsChannelUser } from 'utils'
import { useCurrentUserQuery } from 'generated/graphql'
import ScreenChannelSearchModal from '@app.feature/channel/channel.search/screen/ScreenChannelSearchModal'
import useIsMobile from '@app.modules/hooks/useIsMobile'
import useIsWebview from '@app.modules/hooks/useIsWebview'
import { getWebviewHref } from '@app.modules/util'
import { mobileBreakPoint } from 'app.styled/media'
import LoginAlert from 'components/LoginAlert'
import ChannelCardListBtn from 'svgIcons/ChannelCardListBtn'
import MagnifyingGlass from 'svgIcons/MagnifyingGlass'
import TagFilterBtn from 'svgIcons/TagFilterBtn'
import LineTabs, {
  TabType,
} from '../../../../app.components/tabs/LineTabs/LineTabs'
import TagBottomSheet from '../../channel.feed/screen/menus/ChannelFeedHome/ChannelFeedTagBox/TagBottomSheet'
import ChannelSearchBar from '../ChannelSearchbar'
import ChannelOwnerCard from '../cards/ChannelOwnerCard/ChannelOwnerCard'
import FollowingsModalMobile from '../modals/FollowingsModal/FollowingsModalMobile'

type IFChannelNavbarMobileProps = {
  sticky?: boolean
  searchBar?: 'visible' | 'unvisible' | 'visible-when-stucked'
  initialTab?: number
  disableShadow?: boolean
  className?: string
  type?: 'home' | 'interest'
}

const ChannelNavbarMobile: React.FC<IFChannelNavbarMobileProps> = ({
  sticky = false,
  searchBar = false,
  initialTab,
  className,
  type,
}) => {
  const router = useRouter()
  const [loginModalOpened, setLoginModalOpened] = useState(false)
  const currentUserQuery = useCurrentUserQuery()
  const isMobile = useIsMobile()
  const isWebview = useIsWebview()

  const tabs = [
    {
      id: 0,
      name: '채널홈',
      size: `43px`,
      href: {
        pathname: getWebviewHref('/channel', isWebview),
        query: { tab: 'home' },
      },
      gap: '32px',
    },
    {
      id: 1,
      name: '내 관심 소식',
      size: `77px`,
      href: {
        pathname: getWebviewHref('/channel', isWebview),
        query: { tab: 'interest' },
      },
    },
  ]

  const selectedTagIds = router.query.tags
    ? `${router.query.tags}`.split(',')
    : []

  const visibleSearchBar = useMemo(() => {
    if (searchBar === 'visible') return true
    if (searchBar === 'unvisible') return false
    return searchBar && sticky
  }, [searchBar, sticky])

  const isChannelUser: boolean = useMemo(() => {
    return getIsChannelUser(currentUserQuery.data?.currentUser)
  }, [currentUserQuery.data?.currentUser])

  const tabActive =
    router.pathname === '/channel' || router.pathname === '/webview/channel'

  const handleOpenModal = (modal: string) => {
    if (modal === 'following' && isWebview) {
      router.push('/webview/channel/followings')
    } else if (modal === 'search' && isWebview) {
      router.push({
        pathname: '/webview/channel/search',
        query: {
          back: router.query.back ? router.query.back : router.asPath,
        },
      })
    } else {
      router.push({
        query: {
          ...router.query,
          modal,
        },
      })
    }
  }

  const handleCloseModal = () => {
    router.push({
      query: {
        ...router.query,
        modal: 'close',
      },
    })
  }

  const checkTab = (menu: TabType) => {
    if (menu.id === 1 && !currentUserQuery.data?.currentUser) {
      setLoginModalOpened(true)
      return false
    }
    return true
  }

  return (
    <StyledWrapper className={clsx(className, { sticky })}>
      <div className="nav-content">
        <div className="lt">
          <LineTabs
            className="line-tab"
            data={tabs}
            initialTab={initialTab ? tabs[initialTab] : undefined}
            active={tabActive}
            disableGlider={isMobile}
            checkTab={checkTab}
          />
          {currentUserQuery.data?.currentUser?.channel && (
            <Link
              href={getWebviewHref(
                `/channel/${currentUserQuery.data.currentUser.channel.url}`,
                isWebview,
              )}
            >
              <a className="tab-my-channel">
                {isMobile ? `내 채널` : `내 채널 바로가기`}
                {isMobile && <ArrowRight className="arrow-right" />}
              </a>
            </Link>
          )}
        </div>
        <div
          className={`search-bar-wrapper desktop ${
            visibleSearchBar ? 'visible' : ''
          }`}
        >
          <ChannelSearchBar className="search-bar" />
        </div>
        {isMobile && (
          <div className="search-bar-mobile mobile">
            {type === 'home' && (
              <div
                className="search-modal-btn tag-btn"
                data-active={selectedTagIds.length !== 0}
                onClick={() => handleOpenModal('tag')}
              >
                <div>태그로 보기</div>
                <TagFilterBtn
                  color={selectedTagIds.length === 0 ? '#333' : '#01a0ff'}
                />
              </div>
            )}
            {type === 'interest' && (
              <div
                className="search-modal-btn"
                onClick={() => handleOpenModal('following')}
              >
                <div>팔로우한 채널</div>
                <ChannelCardListBtn />
              </div>
            )}
            <div className="follow-divider"></div>
            <div
              className="search-btn"
              onClick={() => handleOpenModal('search')}
            >
              <MagnifyingGlass />
            </div>
          </div>
        )}
      </div>
      {isMobile && type === 'home' && (
        <TagBottomSheet
          open={router.query.modal === 'tag'}
          onClose={handleCloseModal}
        />
      )}
      {isMobile && type === 'interest' && (
        <FollowingsModalMobile
          open={router.query.modal === 'following'}
          onClose={handleCloseModal}
        />
      )}
      {isMobile && (
        <ScreenChannelSearchModal
          open={router.query.modal === 'search'}
          onClose={handleCloseModal}
        />
      )}
      {isMobile && isChannelUser && <ChannelOwnerCard sticky />}
      <LoginAlert
        DialogProps={{
          open: loginModalOpened,
        }}
        onRequestClose={() => setLoginModalOpened(false)}
      />
    </StyledWrapper>
  )
}

export default ChannelNavbarMobile

const StyledWrapper = styled.div`
  background-color: #fff;
  border-bottom: 1px solid #e8e8e8;
  border-top: 1px solid #eeeeee;

  &.sticky {
    position: sticky;
    top: 61px;
  }
  .nav-content {
    width: 100%;
    max-width: 1148px;
    height: 58px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 auto;
    padding: 0 24px;
    .lt {
      display: flex;
      gap: 32px;

      & > .line-tab {
        height: 58px;
        align-items: center;
        display: flex;
        align-items: center;
        gap: 32px;
      }

      .tab {
        :hover {
          color: #01a0ff;
        }
      }
      .tab-my-channel {
        font-size: 16px;
        display: flex;
        justify-content: center;
        align-items: center;
        white-space: nowrap;
        gap: 2px;
        padding-left: 5px;
        font-weight: 500;
        .arrow-right {
          font-size: 13px;
          padding-top: 1px;
        }

        &::before {
          content: '';
          width: 1px;
          height: 16px;
          background-color: #666666;
          margin-left: -6px;
          margin-right: 20px;
        }
      }
    }
    .search-bar-wrapper {
      opacity: 0;
      padding: 8px 0;
      width: 100%;
      max-width: 780px;
      transform: translate(0px, -60px);
      transition: transform 0.3s ease, opacity 0.3s ease, max-width 0.3s ease;
      &.visible {
        opacity: 1;
        width: 754px;
        transform: translate(0px, 0px);
      }
      .search-bar {
        height: 40px;
        &:focus-within {
          border: 1px solid #01a0ff;
        }
      }
    }
  }
  ${mobileBreakPoint} {
    background-color: #fafafa;
    &.sticky {
      top: 0;
    }
    .nav-content {
      gap: 0;
      padding: 0 20px;
      height: 50px;

      .lt {
        width: 100%;
        gap: 0;

        & > .line-tab {
          height: 50px;
          gap: 10px;
          margin-left: -3px;
          margin-right: 10px;
        }

        .tab {
          width: fit-content;
          padding: 15px 0;
          font-size: 15px;
        }

        .tab-my-channel {
          font-size: 15px;
          gap: 0;
          padding: 0;
          margin-left: -4px;
          .arrow-right {
            width: 12px;
            height: 12px;
            margin-left: 4px;
          }

          &::before {
            display: none;
          }
        }
      }
      .search-bar-mobile {
        display: flex;
        align-items: center;
        flex-shrink: 0;

        .search-modal-btn {
          font-size: 13px;
          align-items: center;
          display: flex;
          flex-shrink: 0;
          cursor: pointer;

          &.tag-btn {
            &[data-active='true'] {
              color: #01a0ff;
              svg {
                path {
                  fill: #01a0ff;
                }
              }
            }
          }
        }
        .follow-divider {
          height: 10px;
          width: 1px;
          background-color: #e8e8e8;
          margin: 0 7px;
        }
        .search-btn {
          cursor: pointer;
          svg {
            width: 16px;
            height: 16px;
          }
        }
      }
    }
  }
`
